import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Globe, MapPin, Waves } from "lucide-react";
import { api } from "@/lib/apiClient";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";

export default function SurfSpotsIndex() {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get("/seo/countries")
      .then((r) => setCountries(r.data.items || []))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-5xl mx-auto p-6 md:p-8" data-testid="surf-spots-index">
      <Seo
        title="Surf spots by country"
        description="Browse curated surf areas by country and region. Suitability, wave conditions, safety and community reports for every published spot."
        path="/surf-spots"
      />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Surf spots" }]} />
      <header className="mb-8">
        <h1 className="text-3xl md:text-5xl font-semibold tracking-tight" data-testid="surf-spots-heading">
          Surf spots by country
        </h1>
        <p className="text-muted-foreground mt-3 max-w-2xl text-base md:text-lg">
          Every location published on Surfmatics — ranked, explained and matched to your
          ability. Pick a country to see its regions and beginner-friendly beaches.
        </p>
      </header>

      {loading ? (
        <div className="text-muted-foreground">Loading…</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {countries.map((c) => (
            <Link
              key={c.slug}
              to={`/surf-spots/${c.slug}`}
              data-testid={`country-card-${c.slug}`}
              className="group rounded-2xl border bg-card hover:bg-accent transition-colors p-5 flex items-start gap-3"
            >
              <div className="w-10 h-10 rounded-xl bg-primary/10 grid place-items-center text-primary shrink-0">
                <Globe className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="font-medium text-lg group-hover:underline underline-offset-4">
                  {c.country}
                </div>
                <div className="text-sm text-muted-foreground mt-1 flex items-center gap-3">
                  <span className="inline-flex items-center gap-1">
                    <Waves className="w-3.5 h-3.5" />
                    {c.spot_count} spot{c.spot_count === 1 ? "" : "s"}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
